import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { getTrackerStats } from '@/api/tracker-stats';
import type { Offer } from '@/api/types';
import { COUNTRY_MAP } from '@/lib/countries';
import { buildOfferGeoCoverage, type GeoRateRange } from '@/lib/offerGeoCoverage';

type CountFilter = '1-3' | 'all' | '1' | '2' | '3';

const FILTERS: Array<{ value: CountFilter; label: string }> = [
  { value: '1-3', label: '1–3' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: 'all', label: 'Все' },
];

const SYMBOLS = { USD: '$', EUR: '€' } as const;

type SortKey = 'geo' | 'offers' | 'regs' | 'deps';
type SortDir = 'asc' | 'desc';

// Direction a column gets when it is first clicked.
const DEFAULT_DIR: Record<SortKey, SortDir> = {
  geo: 'asc',
  offers: 'asc',
  regs: 'desc',
  deps: 'desc',
};

function formatRange(range: GeoRateRange): string {
  const symbol = SYMBOLS[range.currency];
  const min = (range.minCents / 100).toFixed(2);
  const max = (range.maxCents / 100).toFixed(2);
  return range.minCents === range.maxCents ? `${symbol}${min}` : `${symbol}${min}–${symbol}${max}`;
}

export function GeoCoverageModal({
  isOpen,
  onClose,
  offers,
}: {
  isOpen: boolean;
  onClose: () => void;
  offers: Offer[];
}) {
  const [countFilter, setCountFilter] = useState<CountFilter>('1-3');
  const [activeOnly, setActiveOnly] = useState(true);
  const [copied, setCopied] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('offers');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // First click on a numeric column sorts descending — the "от большего к
  // меньшему" view is what these columns are read for; GEO starts alphabetical.
  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(DEFAULT_DIR[key]); }
  };

  const { data: countryStats = [], isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['tracker-stats', 'country', 'all-time'],
    queryFn: () => getTrackerStats({ group_by: 'country' }),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const statsByGeo = useMemo(() => new Map(
    countryStats
      .filter((row) => row.group_value)
      .map((row) => [row.group_value!.toUpperCase(), { regs: row.regs, deps: row.deps }]),
  ), [countryStats]);

  const rows = useMemo(() => {
    const allRows = buildOfferGeoCoverage(offers, { activeOnly });
    const filtered = allRows.filter((row) => {
      if (countFilter === 'all') return true;
      if (countFilter === '1-3') return row.offerCount >= 1 && row.offerCount <= 3;
      return row.offerCount === Number(countFilter);
    });
    const sign = sortDir === 'asc' ? 1 : -1;
    const valueOf = (code: string, count: number): number => {
      const stats = statsByGeo.get(code);
      if (sortKey === 'offers') return count;
      if (sortKey === 'regs') return stats?.regs ?? 0;
      return stats?.deps ?? 0;
    };
    return filtered.sort((a, b) => {
      if (sortKey === 'geo') return sign * a.code.localeCompare(b.code);
      const diff = valueOf(a.code, a.offerCount) - valueOf(b.code, b.offerCount);
      // Ties always fall back to alphabetical GEO, in both directions.
      return diff ? sign * diff : a.code.localeCompare(b.code);
    });
  }, [offers, countFilter, activeOnly, statsByGeo, sortKey, sortDir]);

  const copyList = async () => {
    const text = rows.map((row) => {
      const rates = row.rateRanges.length > 0
        ? row.rateRanges.map(formatRange).join(', ')
        : 'ставка не указана';
      const stats = statsByGeo.get(row.code);
      const regs = statsError && !stats ? '—' : (stats?.regs ?? 0).toLocaleString('ru-RU');
      const deps = statsError && !stats ? '—' : (stats?.deps ?? 0).toLocaleString('ru-RU');
      return `${row.code}: ${row.offerCount} офф. · ${regs} рег. · ${deps} деп. · ${rates}`;
    }).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');
  const thBase = 'px-2 py-2 font-medium cursor-pointer select-none hover:text-content-muted';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Покрытие офферами по GEO" size="2xl">
      <div>
        <div className="mb-3 flex flex-wrap items-end gap-4 border-b border-border pb-3">
          <div>
            <label className="mb-1 block text-xs text-content-subtle">Количество активных офферов</label>
            <div className="inline-flex overflow-hidden rounded-md border border-border text-sm">
              {FILTERS.map((filter, index) => (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setCountFilter(filter.value)}
                  className={`px-3 py-1.5 ${index > 0 ? 'border-l border-border' : ''} ${
                    countFilter === filter.value
                      ? 'bg-primary/10 font-medium text-primary'
                      : 'bg-surface text-content-muted hover:bg-surface-muted'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
          <label className="flex cursor-pointer items-center gap-2 pb-1.5 text-sm text-content-muted">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(event) => setActiveOnly(event.target.checked)}
              className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
            />
            Только активные офферы
          </label>
          <span className="ml-auto pb-1.5 text-xs text-content-subtle">
            Мульти-GEO оффер учитывается в каждом своём GEO. Ставки без значения не входят в range.
          </span>
        </div>

        <div className="max-h-[60vh] overflow-auto">
          {rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-content-subtle">Нет GEO с таким количеством активных офферов.</p>
          ) : (
            <table className="w-full min-w-[42rem] text-sm">
              <thead className="sticky top-0 bg-surface text-left text-xs uppercase tracking-wide text-content-subtle">
                <tr className="border-b border-border">
                  <th className={`${thBase} text-left`} onClick={() => toggleSort('geo')}>GEO{arrow('geo')}</th>
                  <th className={`${thBase} text-right`} onClick={() => toggleSort('offers')}>Офферы{arrow('offers')}</th>
                  <th className={`${thBase} text-right`} onClick={() => toggleSort('regs')} title="Регистрации за всё время">Рег.{arrow('regs')}</th>
                  <th className={`${thBase} text-right`} onClick={() => toggleSort('deps')} title="Депозиты за всё время">Деп.{arrow('deps')}</th>
                  <th className="px-2 py-2 text-right font-medium">Range ставок</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const country = COUNTRY_MAP[row.code];
                  const stats = statsByGeo.get(row.code);
                  const statsUnavailable = statsError && !stats;
                  return (
                    <tr key={row.code} className="border-b border-border last:border-b-0">
                      <td className="px-2 py-2.5">
                        <div className="flex items-center gap-2">
                          {country?.flag && <span aria-hidden>{country.flag}</span>}
                          <span className="font-medium text-content">{row.code}</span>
                          <span className="truncate text-content-muted">{country?.name || row.code}</span>
                        </div>
                      </td>
                      <td className="px-2 py-2.5 text-right font-semibold tabular-nums text-content">{row.offerCount}</td>
                      <td
                        className="px-2 py-2.5 text-right tabular-nums text-content-muted"
                        title={statsUnavailable ? 'Не удалось загрузить статистику' : 'Регистрации за всё время'}
                      >
                        {statsLoading ? '…' : statsUnavailable ? '—' : (stats?.regs ?? 0).toLocaleString('ru-RU')}
                      </td>
                      <td
                        className="px-2 py-2.5 text-right tabular-nums text-content-muted"
                        title={statsUnavailable ? 'Не удалось загрузить статистику' : 'Депозиты за всё время'}
                      >
                        {statsLoading ? '…' : statsUnavailable ? '—' : (stats?.deps ?? 0).toLocaleString('ru-RU')}
                      </td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-content-muted">
                        {row.rateRanges.length > 0
                          ? row.rateRanges.map(formatRange).join(' · ')
                          : <span className="text-content-subtle">не указаны</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={copyList} disabled={rows.length === 0 || statsLoading}>
            {copied ? 'Скопировано!' : 'Копировать список'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>Закрыть</Button>
        </div>
      </div>
    </Modal>
  );
}
